import {
  Box,
  Button,
  Flex,
  Icon,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import {
  AlertTriangle,
  ArrowRight,
  CheckCircle2,
  ChevronRight,
  CloudOff,
  Sparkles,
  Unlink,
} from 'lucide-react';
import React from 'react';
import { DadosPendencias } from '../../../services/hooks/useDashboard';
import formatValue from '../../../utils/formatValue';
import { plural } from '../formatacao';
import Painel from './Painel';

export type TipoPendencia =
  | 'semcorrespondencia'
  | 'divergencias'
  | 'contassematualizacao'
  | 'possiveiscorrespondencias';

interface Props {
  dados?: DadosPendencias;
  isLoading: boolean;
  isError: boolean;
  error?: unknown;
  onRetry: () => void;
  onAbrir: (tipo: TipoPendencia, conta: string | null) => void;
  onVerTodas: () => void;
}

// Mesmos status e cores do ActivityCard do Analytics.
type Status = 'warning' | 'error' | 'info';

const CORES_STATUS: Record<Status, { bg: string; color: string }> = {
  warning: { bg: 'yellow.100', color: 'yellow.800' },
  error: { bg: 'red.100', color: 'red.800' },
  info: { bg: 'blue.100', color: 'blue.800' },
};

interface ItemPendencia {
  tipo: TipoPendencia;
  icon: React.ElementType;
  status: Status;
  titulo: string;
  detalhe: string;
  valor?: string;
  conta: string | null;
}

function itensDe(dados?: DadosPendencias): ItemPendencia[] {
  if (!dados) return [];
  const {
    semcorrespondencia,
    divergencias,
    contassematualizacao,
    possiveiscorrespondencias,
  } = dados;
  const itens: ItemPendencia[] = [];

  if (semcorrespondencia.quantidade > 0) {
    itens.push({
      tipo: 'semcorrespondencia',
      icon: Unlink,
      status: 'warning',
      titulo: plural(
        semcorrespondencia.quantidade,
        'lançamento sem correspondência',
        'lançamentos sem correspondência',
      ),
      detalhe: 'Nenhum título, Saque, Depósito ou Transferência combina',
      valor: semcorrespondencia.valor,
      conta: semcorrespondencia.conta,
    });
  }
  if (divergencias.quantidade > 0) {
    itens.push({
      tipo: 'divergencias',
      icon: AlertTriangle,
      status: 'error',
      titulo: plural(
        divergencias.quantidade,
        'lançamento com divergência',
        'lançamentos com divergência',
      ),
      detalhe: 'Conciliados com valor diferente do extrato',
      valor: divergencias.valor,
      conta: divergencias.conta,
    });
  }
  if (contassematualizacao.quantidade > 0) {
    itens.push({
      tipo: 'contassematualizacao',
      icon: CloudOff,
      status: 'warning',
      titulo: `${plural(
        contassematualizacao.quantidade,
        'conta sem atualização',
        'contas sem atualização',
      )} há mais de 24h`,
      detalhe: contassematualizacao.contas.map(c => c.descricao_cts).join(', '),
      conta: contassematualizacao.contas[0]?.id_cts ?? null,
    });
  }
  if (possiveiscorrespondencias.quantidade > 0) {
    itens.push({
      tipo: 'possiveiscorrespondencias',
      icon: Sparkles,
      status: 'info',
      titulo: plural(
        possiveiscorrespondencias.quantidade,
        'lançamento com possível correspondência',
        'lançamentos com possível correspondência',
      ),
      detalhe: 'A Conciliação Automática já encontra o par',
      valor: possiveiscorrespondencias.valor,
      conta: possiveiscorrespondencias.conta,
    });
  }
  return itens;
}

const AttentionPanel: React.FC<Props> = ({
  dados,
  isLoading,
  isError,
  error,
  onRetry,
  onAbrir,
  onVerTodas,
}) => {
  const bg = useColorModeValue('white', 'gray.800');
  const lineBg = useColorModeValue('gray.100', 'rgb(32,38,50)');
  const hoverBg = useColorModeValue('gray.200', 'gray.900');
  const textColor = useColorModeValue('gray.700', 'white');
  const itens = itensDe(dados);

  return (
    <Painel
      titulo="Requer sua atenção"
      isLoading={isLoading}
      isError={isError}
      error={error}
      onRetry={onRetry}
      h="420px"
      acao={
        <Button
          variant="link"
          size="sm"
          colorScheme="blue"
          rightIcon={<ArrowRight size={14} />}
          onClick={onVerTodas}
        >
          Ver todas as pendências
        </Button>
      }
    >
      {itens.length === 0 ? (
        <Flex
          h="100%"
          minH="200px"
          direction="column"
          align="center"
          justify="center"
          gridGap="2"
        >
          <CheckCircle2 size={32} color="#38A169" />
          <Text fontWeight="medium" color={textColor} mt="2">
            Tudo em dia
          </Text>
          <Text fontSize="sm">Nenhuma pendência no período selecionado</Text>
        </Flex>
      ) : (
        <Box>
          {itens.map((item, i) => {
            const cores = CORES_STATUS[item.status];
            return (
              <Flex
                key={item.tipo}
                as="button"
                type="button"
                w="100%"
                textAlign="left"
                bg={i % 2 === 0 ? lineBg : bg}
                align="center"
                justify="space-between"
                p="3"
                borderRadius="lg"
                _hover={{ backgroundColor: hoverBg }}
                transition="background 0.2s"
                onClick={() => onAbrir(item.tipo, item.conta)}
              >
                <Flex align="center" minW="0">
                  <Flex
                    flexShrink={0}
                    w="10"
                    h="10"
                    mr="3"
                    borderRadius="full"
                    align="center"
                    justify="center"
                    bg={cores.bg}
                    color={cores.color}
                  >
                    <Icon as={item.icon} boxSize="5" />
                  </Flex>
                  <Box minW="0">
                    <Text color={textColor} fontWeight="medium">
                      {item.titulo}
                    </Text>
                    <Text fontSize="sm" noOfLines={1}>
                      {item.detalhe}
                    </Text>
                  </Box>
                </Flex>
                <Flex align="center" flexShrink={0} ml="3" gridGap="2">
                  {item.valor && (
                    <Text
                      color={textColor}
                      fontWeight="medium"
                      whiteSpace="nowrap"
                    >
                      {formatValue(item.valor)}
                    </Text>
                  )}
                  <ChevronRight size={16} />
                </Flex>
              </Flex>
            );
          })}
        </Box>
      )}
    </Painel>
  );
};

export default AttentionPanel;
